import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { TemplateResolve } from 'webapp-types';
import { DATASET_GROUP_TYPE_MERGED } from 'webapp/const/template-resolve';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputSearchComponent from 'webapp/components/form/input-search';
import SubmitButtonComponent from 'webapp/components/submit-button';

export type DatasetSelection = 'create_new' | number;

interface Args {
	group: TemplateResolve.DatasetGroupInfo;
	compatibleDatasets: TemplateResolve.CompatibleDatasetItem[];
	isLoading: boolean;
	stepIndex: number;
	totalSteps: number;
	initialSelection?: DatasetSelection;
	onContinue: (selection: DatasetSelection) => void;
	onSkip: () => void;
	onBack: (currentSelection: DatasetSelection) => void;
}

export default class TemplatesPopupResolveDatasetComponent extends Component<Args> {
	@tracked private _userSelection: DatasetSelection | null = null;
	@tracked private _selectionStepIndex: number | null = null;
	@tracked searchQuery = '';

	get selectedOption(): DatasetSelection {
		if (this._selectionStepIndex === this.args.stepIndex && this._userSelection !== null) {
			return this._userSelection;
		}
		return this.args.initialSelection ?? 'create_new';
	}

	set selectedOption(value: DatasetSelection) {
		this._userSelection = value;
		this._selectionStepIndex = this.args.stepIndex;
	}

	get groupTitle(): string {
		return this.args.group.sourceName || this.args.group.objectName || 'Dataset';
	}

	get metricNames(): string {
		return this.args.group.metrics.map((m) => m.name).join(', ');
	}

	get metricNameItems() {
		const metrics = this.args.group.metrics;
		return metrics.map((m, i) => ({
			name: m.name,
			prefix: i === 0 ? '' : i === metrics.length - 1 ? ' and ' : ', ',
		}));
	}

	get sourceDescriptionNames(): string[] {
		const group = this.args.group;
		if (group.type === DATASET_GROUP_TYPE_MERGED && group.requiredSources?.length) {
			const sourceNames = group.requiredSources.map(
				(s) => s.sourceName || s.objectName || 'unknown',
			);
			const allSame = sourceNames.every((n) => n === sourceNames[0]);
			if (allSame) return [sourceNames[0]!];
			return sourceNames;
		}
		if (group.sourceName) return [group.sourceName];
		if (group.objectName) return [group.objectName];
		return [];
	}

	get sourceDescriptionItems() {
		const names = this.sourceDescriptionNames;
		return names.map((name, i) => ({
			name,
			prefix: i === 0 ? '' : i === names.length - 1 ? ' and ' : ', ',
		}));
	}

	get hasSourceDescription(): boolean {
		return this.sourceDescriptionNames.length > 0;
	}

	get filteredDatasets(): TemplateResolve.CompatibleDatasetItem[] {
		const query = this.searchQuery.toLowerCase().trim();
		if (!query) {
			return this.args.compatibleDatasets;
		}

		return this.args.compatibleDatasets.filter((ds) => ds.title.toLowerCase().includes(query));
	}

	get isMergedDataset(): boolean {
		return this.args.group.type === DATASET_GROUP_TYPE_MERGED;
	}

	get stepLabel(): string {
		return `Step ${this.args.stepIndex + 1} of ${this.args.totalSteps}`;
	}

	get showStepLabel(): boolean {
		return this.args.totalSteps >= 2;
	}

	get hasDatasets(): boolean {
		return this.args.compatibleDatasets.length > 0;
	}

	get isCreateNewSelected(): boolean {
		return this.selectedOption === 'create_new';
	}

	isDatasetSelected = (dataset: TemplateResolve.CompatibleDatasetItem): boolean => {
		return this.selectedOption === dataset.spaceAccessId;
	};

	get isContinueDisabled(): boolean {
		if (!this.args.group.templateId && this.selectedOption === 'create_new') {
			return true;
		}
		return false;
	}

	@action
	selectCreateNew(): void {
		if (this.args.group.templateId) {
			this.selectedOption = 'create_new';
		}
	}

	@action
	selectDataset(dataset: TemplateResolve.CompatibleDatasetItem): void {
		if (dataset.isCompatible) {
			this.selectedOption = dataset.spaceAccessId;
		}
	}

	@action
	onSearchChange(value: string): void {
		this.searchQuery = value;
	}

	@action
	handleBack(): void {
		this.args.onBack(this.selectedOption);
	}

	@action
	handleContinue(): void {
		this.args.onContinue(this.selectedOption);
	}

	<template>
		<div class="title">
			<CommonUiIconComponent @name="DataManager" @size="lg" />
			Use existing or create new dataset
		</div>

		<div class="content">
			{{#if this.showStepLabel}}
				<p class="resolve-dataset__step-label">
					{{this.stepLabel}}
				</p>
			{{/if}}
			<p>
				{{#if this.hasSourceDescription}}
					Metrics
					{{#each this.metricNameItems as |item|}}
						{{~item.prefix~}}<strong>{{item.name}}</strong>
					{{~/each}}
					are working with a
					{{if this.isMergedDataset "merged "}}dataset built from
					{{#each this.sourceDescriptionItems as |item|}}
						{{~item.prefix~}}<strong>{{item.name}}</strong>
					{{~/each}}.
					{{#if this.hasDatasets}}
						You already have at least one created, please select how you want to
						proceed:
					{{/if}}
				{{else}}
					The template uses a
					{{if this.isMergedDataset "merged "}}dataset with the following metrics:
					<em>{{this.metricNames}}</em>.
				{{/if}}
			</p>

			{{#if @isLoading}}
				<div class="resolve-dataset__loading">
					Loading compatible datasets...
				</div>
			{{else}}
				<div class="resolve-dataset__options">
					{{#if this.hasDatasets}}
						<div class="resolve-dataset__search">
							<FormInputSearchComponent
								@value={{this.searchQuery}}
								@onChange={{this.onSearchChange}}
								@placeholder="Search existing datasets..."
								@size="sm"
							/>
						</div>

						<div class="resolve-dataset__list">
							{{#each this.filteredDatasets as |dataset|}}
								<label
									class="resolve-dataset__option
										{{if (this.isDatasetSelected dataset) 'is-selected'}}
										{{unless dataset.isCompatible 'is-incompatible'}}"
									{{on "click" (fn this.selectDataset dataset)}}
								>
									<input
										type="radio"
										name="dataset-selection"
										checked={{this.isDatasetSelected dataset}}
										disabled={{unless dataset.isCompatible true}}
										class="resolve-dataset__option-radio"
									/>
									<div class="resolve-dataset__option-content">
										<strong>{{dataset.title}}</strong>
										{{#if dataset.columnsCount}}
											<span class="resolve-dataset__option-meta">
												{{dataset.columnsCount}}
												columns
											</span>
										{{/if}}
										{{#if dataset.createdBy}}
											<span class="resolve-dataset__option-meta">
												by
												{{dataset.createdBy.name}}
											</span>
										{{/if}}
									</div>
									{{#unless dataset.isCompatible}}
										<span class="resolve-dataset__option-warning">
											Dataset is not compatible
										</span>
									{{/unless}}
								</label>
							{{/each}}
						</div>
					{{/if}}

					<label
						class="resolve-dataset__option resolve-dataset__option--create-new
							{{if this.isCreateNewSelected 'is-selected'}}
							{{unless @group.templateId 'is-incompatible'}}"
						{{on "click" this.selectCreateNew}}
					>
						<input
							type="radio"
							name="dataset-selection"
							checked={{this.isCreateNewSelected}}
							disabled={{unless @group.templateId true}}
							class="resolve-dataset__option-radio"
						/>
						<div class="resolve-dataset__option-content">
							<strong>Resolve automatically</strong>
							<span class="resolve-dataset__option-description">
								{{#if @group.templateId}}
									Uses an existing matching dataset or creates a new one. The
									selected data source must have a compatible structure (i.e. same
									column names and types) for the dataset to work correctly.
								{{else}}
									Not available — the original dataset used to create this
									template no longer exists.
								{{/if}}
							</span>
						</div>
					</label>
				</div>
			{{/if}}

			<div class="controls2">
				<div class="controls2__left">
					<SubmitButtonComponent
						@text="Back"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.handleBack}}
					/>
					<SubmitButtonComponent
						@text="Skip this dataset"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{@onSkip}}
						@tooltipText="Metrics from this dataset will not be resolved automatically. The dashboard will contain empty blocks that you will need to configure manually."
					/>
				</div>
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Continue"
						@isDisabled={{this.isContinueDisabled}}
						@onTrigger={{this.handleContinue}}
						@extraClass="continue"
					/>
				</div>
			</div>
		</div>
	</template>
}
